import { FC, useMemo } from 'react';

import { Formik, type FormikHelpers } from 'formik';
import { Company } from 'utils/types';
import { Loading } from 'pages';

import CreateCompanyForm from '../forms/CompanyForm';
import { COMPANY_FORM, COMPANY_SCHEMA } from '../forms/schema';
import useUpdateCompany from '../hooks/useUpdateCompany';
import { useGetCompanyContactDetails } from '../hooks';

type PROPS = {
  id: string;
};

const UpdateCompanyModal: FC<PROPS> = ({ id = '' }) => {
  const { mutate: updateCompany } = useUpdateCompany();

  const { data: companyData, isFetching } = useGetCompanyContactDetails(id, {
    include: ['dataList'],
  });
  const onSubmit = (
    values: COMPANY_FORM,
    { setSubmitting }: FormikHelpers<COMPANY_FORM>,
  ) => {
    updateCompany(
      { id, ...values },
      {
        onSettled: () => {
          setSubmitting(false);
        },
        onError: () => {
          setSubmitting(false);
        },
      },
    );
  };

  const initialValues = useMemo(() => {
    const {
      name,
      industry,
      type,
      city,
      state,
      postalCode,
      noEmployees,
      annualRevenue,
      timezone,
      description,
      linkedinUrl,
      companyWebsite,
      tagId,
      data,
      ...rest
    } = companyData ?? ({} as Company);
    return {
      name,
      industry,
      type,
      city,
      state,
      postalCode,
      noEmployees,
      annualRevenue,
      timezone,
      description,
      linkedinUrl,
      companyWebsite,
      tagId,
      data: data?.map((d) => ({ ...d, error: '' })),
      ...rest,
    } as COMPANY_FORM;
  }, [companyData]);

  if (!companyData || isFetching) {
    return <Loading />;
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={onSubmit}
      validationSchema={COMPANY_SCHEMA}
    >
      {() => <CreateCompanyForm />}
    </Formik>
  );
};

export default UpdateCompanyModal;
